/**
 * Boton de Google, cargado solo cuando hace falta.
 *
 * El script de Google se pide la PRIMERA vez que se va a usar, no al arrancar:
 * casi todas las partidas empiezan sin tocar la cuenta, y cargarlo siempre
 * seria una peticion a accounts.google.com por cada jugador que ni lo mira.
 * Tambien es lo correcto en privacidad: no se contacta con Google hasta que el
 * jugador entra en su carrera.
 *
 * El Client ID lo manda el SERVER en `welcome`. Si no lo manda, esto no se
 * monta y el jugador ni ve el boton, igual que el resto de la tarjeta de cuenta.
 */
interface GoogleIdApi {
  initialize(options: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

const SCRIPT_URL = "https://accounts.google.com/gsi/client";
let loading: Promise<GoogleIdApi> | null = null;

function loadScript(): Promise<GoogleIdApi> {
  if (loading) return loading;
  loading = new Promise<GoogleIdApi>((resolve, reject) => {
    const existing = window.google?.accounts?.id;
    if (existing) {
      resolve(existing);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const api = window.google?.accounts?.id;
      if (api) resolve(api);
      else reject(new Error("gsi sin api"));
    };
    // Sin conexion, con un bloqueador o en un pais donde Google no llegue: el
    // juego sigue. Para eso esta el codigo de recuperacion.
    script.onerror = () => reject(new Error("no se pudo cargar gsi"));
    document.head.appendChild(script);
  });
  return loading;
}

export interface GoogleButtonOptions {
  clientId: string;
  parent: HTMLElement;
  onCredential(credential: string): void;
  onUnavailable(): void;
}

/** Monta el boton oficial de Google dentro de `parent`. */
export async function mountGoogleButton(options: GoogleButtonOptions): Promise<void> {
  let api: GoogleIdApi;
  try {
    api = await loadScript();
  } catch {
    options.onUnavailable();
    return;
  }

  api.initialize({
    client_id: options.clientId,
    callback: (response) => {
      if (response.credential) options.onCredential(response.credential);
    },
    // Nada de entrar solo: la cuenta se conecta cuando el jugador lo pide, no
    // porque el navegador recuerde una sesion de Google de otra web.
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  options.parent.innerHTML = "";
  api.renderButton(options.parent, {
    type: "standard",
    theme: "filled_black",
    size: "large",
    text: "continue_with",
    shape: "pill",
    locale: "es",
  });
}
